import {
  INDEX_DB_CAROUSEL,
  INDEX_DB_NAME,
  INDEX_DB_SEARCH_STORE,
  INDEX_DB_VERSION,
} from '@/utilities/constant'
import { openDB } from 'idb'

type CachedCarouselResult<T> = {
  key: string
  data: T
  updatedAt: number
}

const STORE_NAME = INDEX_DB_CAROUSEL
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30

export const PRODUCT_CAROUSEL_CACHE_KEY = 'products-carousel:v2:limit100'
const LS_PREFIX = 'gallium:carousel-cache:'

async function getDb() {
  return openDB(INDEX_DB_NAME, INDEX_DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(INDEX_DB_SEARCH_STORE)) {
        db.createObjectStore(INDEX_DB_SEARCH_STORE, { keyPath: 'key' })
      }
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' })
      }
    },
  })
}

export async function writeProductCarouselCache<T>(key: string, data: T): Promise<void> {
  if (typeof window === 'undefined') return

  const value: CachedCarouselResult<T> = {
    key,
    data,
    updatedAt: Date.now(),
  }

  // Primary storage: IndexedDB.
  try {
    const db = await getDb()
    await db.put(STORE_NAME, value)
  } catch {
    // Ignore and still attempt fallback storage.
  }

  // Fallback storage: localStorage (helps when IDB is blocked/unstable in PWA contexts).
  try {
    localStorage.setItem(`${LS_PREFIX}${key}`, JSON.stringify(value))
  } catch {
    // Ignore quota/security issues.
  }
}

export async function readProductCarouselCache<T>(
  key: string,
  options?: { allowStale?: boolean },
): Promise<T | null> {
  if (typeof window === 'undefined') return null

  let value: CachedCarouselResult<T> | undefined

  // Try IndexedDB first.
  try {
    const db = await getDb()
    value = (await db.get(STORE_NAME, key)) as CachedCarouselResult<T> | undefined
  } catch {
    // Fallback below.
  }

  // Fallback to localStorage if IDB misses/fails.
  if (!value) {
    try {
      const raw = localStorage.getItem(`${LS_PREFIX}${key}`)
      if (raw) value = JSON.parse(raw) as CachedCarouselResult<T>
    } catch {
      // ignore parsing/security errors
    }
  }

  if (!value) return null

  if (Date.now() - value.updatedAt > MAX_AGE_MS) {
    if (options?.allowStale) return value.data
    try {
      const db = await getDb()
      await db.delete(STORE_NAME, key)
    } catch {
      // ignore
    }
    try {
      localStorage.removeItem(`${LS_PREFIX}${key}`)
    } catch {
      // ignore
    }
    return null
  }

  return value.data
}
